import { QueryCache, QueryClient, MutationCache } from '@tanstack/react-query';
import { ApiError } from '~/lib/api-client';
import { Sentry } from '~/sentry';

/** The HTTP status off an `ApiError`, or undefined for anything else (a
 *  network failure, or a genuine JS error thrown inside a queryFn). */
function statusOf(error: unknown): number | undefined {
  if (error instanceof Error && 'status' in error) {
    const status = (error as { status?: unknown }).status;
    if (typeof status === 'number') return status;
  }
  return undefined;
}

/**
 * A failure of the transport, not of our code: the request never got an
 * answer. Matching on the message is ugly but it is all `fetch` gives us —
 * there is no error code — and the wording is browser-specific: Chrome says
 * "Failed to fetch", Firefox "NetworkError when attempting to fetch
 * resource.", Safari "Load failed". Aborts/timeouts land here too: the caller
 * cancelled (unmount, navigation) or an AbortSignal.timeout fired.
 *
 * Deliberately narrow — a plain `TypeError` from a real bug in a queryFn
 * ("x is not a function") does NOT match and still reaches Sentry.
 */
function isNetworkError(error: unknown): boolean {
  if (error instanceof TypeError) {
    return /failed to fetch|networkerror|load failed/i.test(error.message);
  }
  return (
    error instanceof Error && (error.name === 'AbortError' || error.name === 'TimeoutError')
  );
}

/**
 * FRONT-1520/1521: errors no frontend change can act on, so reporting them
 * only buries the ones that can be. Together these were ~80% of the eddy
 * Sentry project's volume (~600 events/week across 21 issues): every
 * "ApiError: Rate limit exceeded" and every "TypeError: Failed to fetch".
 *
 * - 429 is the server telling a healthy client to back off. The fix is the
 *   backoff (and the per-user bucket in src/http/server.ts), not a code change
 *   here — an alert per occurrence is pure noise.
 * - A network failure is the user's connection, a tab navigating away
 *   mid-poll, or a deploy restarting the server. Real outages surface on the
 *   backend project and in uptime monitoring, which can actually act on them.
 * - An EXPECTED 401 (see `unauthorized()` in api-client) is a session that
 *   expired or was never established. The app answers it by routing to login;
 *   there is nothing to fix. This was the single largest remaining source of
 *   auto-filed tickets — fourteen of them, all the same stack, all "the user
 *   was signed out". An UNEXPECTED 401 — the silent refresh succeeded and the
 *   route refused anyway — is a genuine authorization defect and still
 *   reports, which is why the distinction is made at the throw site rather
 *   than by matching on the status here.
 *
 * Still console.error'd below, so it remains visible in devtools when
 * debugging a specific session.
 */
function isExpectedAuthFailure(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401 && error.expectedAuthFailure;
}

/**
 * FRONT-2197: the server marked this refusal as written for the PERSON.
 *
 * A status code cannot tell the two kinds of 4xx apart. A 400 is our bug when
 * it means "that payload was malformed", and is not a bug at all when it means
 * "you cannot call someone 'der Gast', that is a role and not a name" — a
 * sentence the UI shows as a toast and that no frontend change would prevent.
 * That one guard alone was 882 events in a week (EDDY-30), and the old rule
 * here reported every 400 on the reasoning that a malformed payload IS our
 * bug — true, but only of the other kind.
 *
 * So the decision is made where the sentence is written, not guessed from the
 * status: `refusal()` in src/http/server.ts sets `userFacing` on the body, and
 * this reads it. Anything else on the wire keeps reporting, so a route that has
 * not opted in is unaffected.
 */
export function isUserFacingRefusal(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  const body: unknown = error.body;
  if (typeof body !== 'object' || body === null) return false;
  return (body as { userFacing?: unknown }).userFacing === true;
}

function isUnactionable(error: unknown): boolean {
  return (
    statusOf(error) === 429 ||
    isNetworkError(error) ||
    isExpectedAuthFailure(error) ||
    isUserFacingRefusal(error)
  );
}

/**
 * FRONT-1540: how a reported ApiError is grouped in Sentry.
 *
 * Every ApiError is thrown from the single `request()` in api-client, so
 * Sentry's default stack-trace grouping collapsed all of them into ONE issue
 * whose title is whichever message happened to arrive last. Observed live:
 * the project's top issue read "ApiError: Tool not found" (691 events) one day
 * and "That approval is no longer open" (120 events) the next — the same group,
 * silently holding unrelated bugs and reporting the count as if it were one.
 *
 * Grouping by status and path splits it back apart. The path has to be
 * normalized first or the opposite failure appears: `/api/chats/<uuid>` would
 * mint a fresh issue per chat and bury the signal in thousands of one-event
 * groups. Anything that looks like an identifier collapses to `:id`.
 */
export function normalizeApiPath(path: string): string {
  return path
    .split('?')[0]
    .split('/')
    .map((segment) => {
      if (!segment) return segment;
      // UUID, or any long/opaque or numeric segment — all identifiers.
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)) return ':id';
      if (/^\d+$/.test(segment)) return ':id';
      // Mixed-case or digit-bearing tokens past a sane length are slugs/ids,
      // not route words. Kept generous so real route names ("background-chats",
      // "custom-tool-drafts") stay legible in the issue title.
      if (segment.length > 24 && /\d/.test(segment)) return ':id';
      return segment;
    })
    .join('/');
}

/** The Sentry fingerprint for an ApiError, or undefined to keep the default. */
export function apiErrorFingerprint(error: unknown): string[] | undefined {
  const status = statusOf(error);
  if (status === undefined) return undefined;
  const path = (error as { path?: unknown }).path;
  return [
    'api-error',
    String(status),
    typeof path === 'string' && path ? normalizeApiPath(path) : 'unknown-path',
  ];
}

/**
 * FRONT-2197: does this path name ONE specific thing, rather than a route?
 *
 * `normalizeApiPath` already collapses every identifier-shaped segment to
 * `:id`, so the question is just whether it produced one. `/api/graph/entity/
 * :id` names a thing; `/api/graph/entities` names a route. That distinction is
 * what separates the two ways a read can 404 — see `isGoneOnRead` below.
 */
function namesOneResource(error: unknown): boolean {
  const path: unknown = (error as { path?: unknown }).path;
  if (typeof path !== 'string' || !path) return false;
  return normalizeApiPath(path).split('/').includes(':id');
}

/**
 * FRONT-2197: a READ whose subject no longer exists.
 *
 * `RACE_LOST_STATUSES` below suppresses this for mutations already, and left
 * reads reporting on the reasoning that nobody clicked a query, so a 404 there
 * could be a real routing mistake. That holds for `/api/graph/entities` — if a
 * route word is wrong, we want to hear about it. It does not hold for
 * `/api/graph/entity/<uuid>`: the route resolved fine and the ENTITY is gone,
 * deleted or merged away while a panel still held its id. There is no frontend
 * change that prevents it and the UI already says so. Observed at 2,171 events
 * (EDDY-2V), all from the Work page's entity panel.
 *
 * So the id is the discriminator: a 404/410 on a path naming one resource is
 * the thing being gone, and a 404 on a path without one is still a routing bug
 * and still reports.
 *
 * SCOPE — this is app-wide, and deliberately so (PR #1528 review). It is not
 * a graph rule: it fires on every id-scoped read in the app — files, chats,
 * work containers, apps, cron jobs, skills — all of which reach an id whose
 * row a person, a colleague, a purge or a merge can delete between the id
 * being held and the read going out. Every one of those wants the same
 * treatment, so narrowing this to graph paths would leave the identical noise
 * to be rediscovered per surface. `readSuppressionScope` in the tests pins
 * that breadth, so a later narrowing has to be deliberate.
 *
 * THE COST, stated plainly: a route-matching regression on an id-scoped GET —
 * a bad regex, a dropped registration — used to surface here as a Sentry
 * spike and now does not, because the path still names one resource. Accepted
 * for two reasons. It is not separable: an unregistered route answers the
 * shared `NOT_FOUND_BODY`, which `machines` and `operator` deliberately return
 * too so that a route being absent cannot be told from a row not being yours
 * (`src/core/not-found.ts`) — keying on it would buy this signal back by
 * reporting those existence-hiding 404s instead. And it is not silent
 * elsewhere: such a regression 404s EVERY id on that route at once, which
 * breaks the surface for everyone and fails the browser specs, rather than
 * producing the sporadic per-row misses this rule exists to swallow.
 */
function isGoneOnRead(error: unknown): boolean {
  const status = statusOf(error);
  if (status !== 404 && status !== 410) return false;
  return namesOneResource(error);
}

function handleGlobalError(error: unknown): void {
  console.error('[Query Error]', error);
  if (!isUnactionable(error) && !isGoneOnRead(error)) {
    const fingerprint = apiErrorFingerprint(error);
    Sentry.captureException(error, {
      tags: { source: 'query' },
      // Only set it when we have one — leaving `fingerprint: undefined` in the
      // scope context relies on how Sentry treats an explicit undefined.
      ...(fingerprint ? { fingerprint } : {}),
    });
  }
  // Don't auto-redirect to login on 401. A single endpoint failing — bad
  // route, missing claim, race — shouldn't drag the whole session out. Route
  // guards / the login page should gate on /api/auth/me explicitly when they
  // need to enforce auth.
}

function shouldRetry(failureCount: number, error: unknown): boolean {
  const status = statusOf(error);
  if (status === 401 || status === 403) return false;
  // FRONT-218: a refusal written for the person (e.g. the org's sandbox cap,
  // a 503) is an answer, not a flaky connection.
  if (isUserFacingRefusal(error)) return false;
  // FRONT-1521: never retry a 429. The bucket is already empty, so each retry
  // spends a token it doesn't have and keeps it empty — with ~40 polling
  // queries in the app, one brief burst over the limit turned into a
  // self-sustaining storm (1 genuine 429 became 4 requests, every poll
  // interval, for as long as the page stayed open). This is why the 429s in
  // Sentry arrived in hours-long clusters rather than as isolated spikes.
  if (status === 429) return false;
  // FRONT-1410: a 404 is an answer, not a failure — the thing is gone, and the
  // identical request will keep saying so. Retrying it three times with growing
  // backoff bought nothing and cost ~6-7 seconds of the user's time: deleting a
  // skill invalidated the deleted skill's OWN detail query, and the editor
  // popup, whose close was chained behind that refetch, sat on screen for the
  // whole retry sequence. 410 is the same statement, said more explicitly.
  if (status === 404 || status === 410) return false;
  return failureCount < 3;
}

/**
 * FRONT-1088: mutations are writes, not idle reads — a definitive 4xx
 * response (400 validation, 404 not found, 409 conflict, …) means the exact
 * same payload will fail identically on retry, so auto-retrying only delays
 * the user ever seeing the error. Before this, mutations reused `shouldRetry`
 * (written for queries) and silently retried e.g. a 409 "name already
 * exists" up to 3 times with growing backoff (~1s/2s/4s) before the mutation
 * ever settled — the Work page's "New project" dialog sat on "Creating…"
 * with the submit button disabled and no toast for several seconds, reading
 * as an indefinite hang (this applied to EVERY 4xx on EVERY mutation in the
 * app, not just this one 409 path — it was just never noticed before).
 * Network failures (no parsed status) and 5xx still get the bounded retry a
 * flaky connection deserves.
 */
function shouldRetryMutation(failureCount: number, error: unknown): boolean {
  const status = statusOf(error);
  if (isUserFacingRefusal(error)) return false;
  // FRONT-2480: the workflow editor's generated client throws the parsed
  // response BODY (`{ error: "..." }`), not an Error, and drops the status. A
  // body means the server answered, so it is not a flaky connection: a 403 on
  // "New workflow" was retried three times while the button spun.
  if (!(error instanceof Error)) return false;
  if (typeof status === 'number' && status >= 400 && status < 500) return false;
  return failureCount < 3;
}

/**
 * The retry for a file upload: resend only when the request never got an
 * answer, up to three times. A refusal (too big, wrong type) and a server
 * error both mean the server answered, so the person sees why instead of a
 * silent resend that could store the file twice (FRONT-2741). A dropped or
 * stalled connection is the case the retry exists for: before this, a phone
 * photo on mobile data failed on the first blip. A cancel the person tapped
 * (`AbortError`) is their decision and is never resent.
 */
export function retryUploadOnConnectionLoss(failureCount: number, error: unknown): boolean {
  if (failureCount >= 3) return false;
  if (error instanceof Error && error.name === 'AbortError') return false;
  return isNetworkError(error);
}

/**
 * FRONT-1539: a mutation that lost a race with reality. 404, 409 and 410 all
 * say the same thing — the thing you just acted on is gone, or was already
 * decided by someone else. The server answers with a sentence written for a
 * human and the UI shows it, so the user has been told; there is nothing left
 * for an engineer to do.
 *
 * This was the loudest issue in the eddy Sentry project at ~120 events/day:
 * "That approval is no longer open" (src/agents/routes.ts, the deliberate 404
 * from FRONT-1120). Approvals are org-scoped and rendered on three surfaces at
 * once, so one teammate or tab resolving one while another still sees the card
 * is normal operation, not a defect — `approval-decided` over SSE clears cards
 * promptly but cannot close the window between render and click.
 *
 * Deliberately NOT 400/422: a payload the server calls malformed IS our bug.
 * FRONT-2197 narrowed that: a 400 the server MARKED as written for the person
 * is not, and `isUserFacingRefusal` above lets it say so.
 *
 * Deliberately mutations only: nobody clicked a query, so a 404 there can be a
 * real routing mistake worth hearing about. FRONT-2197 narrowed this half too —
 * see `isGoneOnRead`, which excuses a read 404 only when the path names one
 * specific resource, leaving the routing-mistake case reporting.
 */
const RACE_LOST_STATUSES = new Set([404, 409, 410]);

function handleMutationError(error: unknown): void {
  console.error('[Mutation Error]', error);
  const status = statusOf(error);
  const lostRace = status !== undefined && RACE_LOST_STATUSES.has(status);
  if (!isUnactionable(error) && !lostRace) {
    const fingerprint = apiErrorFingerprint(error);
    Sentry.captureException(error, {
      tags: { source: 'mutation' },
      ...(fingerprint ? { fingerprint } : {}),
    });
  }
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({ onError: handleGlobalError }),
    mutationCache: new MutationCache({ onError: handleMutationError }),
    defaultOptions: {
      queries: {
        staleTime: 60_000,
        gcTime: 5 * 60_000,
        retry: shouldRetry,
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: shouldRetryMutation,
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined;

export function getQueryClient(): QueryClient {
  if (!browserQueryClient) {
    browserQueryClient = createQueryClient();
  }
  return browserQueryClient;
}

/**
 * The sentence to show a person for a failed request: the server's own words
 * only when the route wrote them for the PERSON (`refusal(...)`, marked
 * `userFacing`); anything else (a 500's "Internal server error", a network
 * failure's browser text) is replaced by the caller's fixed friendly line.
 */
export function personFacingMessage(error: unknown, fallback: string): string {
  if (isUserFacingRefusal(error) && error instanceof Error && error.message) return error.message;
  return fallback;
}
